import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import usePageTitle from "@/hooks/use-page-title";

const COLUMNS = [
  { key: "design_pending", label: "Design Pending", color: "#71717A" },
  { key: "designing", label: "Designing", color: "#0EA5E9" },
  { key: "design_approval", label: "Design Approval", color: "#EAB308" },
  { key: "design_approved", label: "Design Approved", color: "#22C55E" },
  { key: "printing", label: "Printing", color: "#FF6B00" },
  { key: "finishing", label: "Finishing", color: "#F97316" },
  { key: "quality_check", label: "Quality Check", color: "#8B5CF6" },
  { key: "packing", label: "Packing", color: "#0EA5E9" },
  { key: "ready", label: "Ready", color: "#22C55E" },
];

const NEXT = {
  design_pending: "designing",
  designing: "design_approval",
  design_approval: "design_approved",
  design_approved: "printing",
  printing: "finishing",
  finishing: "quality_check",
  quality_check: "packing",
  packing: "ready",
};

export default function Production() {
  usePageTitle("Production Queue");
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  const load = () => api.get("/jobs").then((r) => setRows(r.data)).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const advance = async (j) => {
    const nxt = NEXT[j.status];
    if (!nxt) return;
    try {
      await api.patch(`/orders/${j.order_id}/jobs/${j.job_id}/status`, { status: nxt });
      load();
    } catch (e) { alert(formatApiError(e)); }
  };

  const grouped = {};
  rows.forEach((j) => {
    grouped[j.status] = grouped[j.status] || [];
    grouped[j.status].push(j);
  });

  return (
    <div className="space-y-6" data-testid="production-root">
      <div className="border-b border-[hsl(var(--border))] pb-6">
        <div className="overline mb-2">Shop Floor</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Production Queue</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Individual jobs across all orders. Each job advances independently — a Flex banner and a Visiting Card in the same order can be at different stages.
        </p>
      </div>
      {err && <div className="text-sm text-red-600">{err}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {COLUMNS.map((c) => (
          <div key={c.key} className="card-swiss p-3" data-testid={`prod-col-${c.key}`}>
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-[hsl(var(--border))]">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider" style={{ color: c.color }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.color }} />
                {c.label}
              </div>
              <div className="text-xs text-muted-foreground font-mono">{(grouped[c.key] || []).length}</div>
            </div>
            <div className="space-y-2 min-h-[80px]">
              {(grouped[c.key] || []).map((j) => (
                <div key={j.job_id} className="border border-[hsl(var(--border))] hover:border-[#FF6B00] p-2 space-y-1" data-testid={`prod-job-${j.job_id}`}>
                  <div className="flex items-center justify-between">
                    <Link to={`/orders/${j.order_id}`} className="font-mono text-[10px] font-bold hover:text-[#FF6B00]">
                      {j.job_no || j.order_id}
                    </Link>
                    {j.priority && j.priority !== "normal" && (
                      <span className="text-[9px] font-bold uppercase text-red-500 border border-red-500 px-1">{j.priority}</span>
                    )}
                  </div>
                  <div className="font-semibold text-xs truncate">{j.customer_name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{j.product_name || j.product_category}</div>
                  {NEXT[j.status] && (
                    <button
                      onClick={() => advance(j)}
                      className="w-full text-[11px] bg-[#FF6B00] text-white py-1 hover:bg-[#e65a00]"
                      data-testid={`prod-advance-${j.job_id}`}
                    >
                      ▶ {COLUMNS.find((x) => x.key === NEXT[j.status])?.label || "Next"}
                    </button>
                  )}
                </div>
              ))}
              {(grouped[c.key] || []).length === 0 && (
                <div className="text-xs text-muted-foreground text-center py-4 opacity-50">Empty</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
